import BackButton from './BackButton';

/**
 * The printable commission document.
 *
 * Shared by Merchant Invoices (what a merchant owes us) and School Pay (what we
 * owe a school), so the two can never drift into two different looking
 * documents. Callers own the data — which transactions, at what rate — and this
 * owns only how it is presented.
 *
 * The wording is caller-supplied because the two directions are not the same
 * document: money coming in is billed, money going out is paid. Defaults are
 * the merchant-invoice wording, so the invoice pages need no props.
 */

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const headCell = {
  textAlign: 'left', padding: '12px 8px', fontSize: '11px', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999',
  whiteSpace: 'nowrap',
};
const headCellRight = { ...headCell, textAlign: 'right' };
const formatPct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;

const monthRangeLabel = (key) => {
  const [y, m] = String(key || '').split('-').map(Number);
  if (!y || !m) return '';
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const monthTitle = (key) => {
  const [y, m] = String(key || '').split('-').map(Number);
  if (!y || !m) return '';
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const invoiceNumberFor = (yearMonth, merchantName, prefix = 'HC') =>
  `${prefix}-${String(yearMonth || '').replace('-', '')}-${String(merchantName || '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 8)
    .toUpperCase()}`;

const printInvoice = () => {
  const dateStr = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const styleId = 'invoice-print-footer-style';
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    @media print {
      @page {
        @bottom-left { content: "${dateStr}"; font-size: 9pt; color: #888; }
      }
    }
  `;
  window.print();
};

const InvoiceSheet = ({
  backTo,
  backText,
  merchantName,
  yearMonth,
  lineItems,
  totals,
  error,
  note,
  // Wording. Defaults describe a merchant invoice; School Pay overrides them.
  documentTitle = 'Invoice',
  documentNumber,
  partyLabel = 'Bill To',
  periodLabel = 'Billing period',
  amountLabel = 'Amount Due',
  // The column the rate is applied to. A merchant is billed a percent of the
  // sale; a school is paid a percent of the cashback, not of the sale.
  baseColumnLabel = 'Sale',
  commissionLabel = 'Commission',
  // Which line columns to draw. A merchant invoice has to show its arithmetic —
  // sale, rate, commission — because the merchant is being asked to pay it. A
  // school statement is a list of what it earned and when, so the working is
  // noise. Defaults keep the invoice exactly as it was.
  dateColumnLabel = 'Date',
  // The processor's cut for collecting the invoice, shown as its own line so a
  // merchant can see why the debit is larger than the commission.
  feeLabel = null,
  feeAmount = 0,
  showMerchantColumn = false,
  showBaseColumn = true,
  showRateColumn = true,
  // Equal-width columns. An invoice wants Date narrow so the arithmetic lines
  // up on the right; a statement has only three columns and reads better with
  // them spread across the sheet.
  evenColumns = false,
  emptyText = 'No transactions for this merchant in this period.',
  // Optional pre-invoice slot (Billing puts its ACH panel here). Excluded from
  // print by the caller's own `invoice-actions` class.
  banner,
}) => {
  const columnCount =
    2 +
    (showMerchantColumn ? 1 : 0) +
    (showBaseColumn ? 1 : 0) +
    (showRateColumn ? 1 : 0);
  const evenWidth = `${(100 / columnCount).toFixed(4)}%`;

  return (
  <>
    <div
      className="invoice-actions"
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px' }}
    >
      <BackButton text={backText} to={backTo} />
      <button
        type="button"
        onClick={printInvoice}
        style={{
          padding: '8px 16px',
          border: '1px solid #dee2e6',
          borderRadius: '100px',
          background: 'white',
          fontFamily: 'inherit',
          fontSize: '13px',
          fontWeight: 500,
          color: '#495057',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#adb5bd'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#dee2e6'; }}
      >
        Print
      </button>
    </div>

    {banner}

    {error && <div style={{ color: '#b00020', marginBottom: '24px' }}>{error}</div>}

    <style>{`
      .invoice-header-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
      }
      .invoice-billto-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
      }
      @media (max-width: 640px) {
        .invoice-sheet { padding: 24px !important; border-radius: 12px !important; }
        .invoice-header-row { flex-direction: column !important; gap: 20px; }
        .invoice-header-right { text-align: left !important; }
        .invoice-billto-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
        .invoice-amount-due { text-align: left !important; }
        .invoice-amount-due-value { font-size: 26px !important; }
        .invoice-brand { font-size: 22px !important; }
        .invoice-title { font-size: 20px !important; }
        .invoice-table th, .invoice-table td { padding: 10px 4px !important; font-size: 13px !important; }
      }
      @media (max-width: 400px) {
        .invoice-sheet { padding: 16px !important; }
        .invoice-table th, .invoice-table td { padding: 8px 3px !important; font-size: 12px !important; }
      }
    `}</style>

    <div
      className="invoice-sheet"
      style={{
        background: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '960px',
      }}
    >
      <div className="invoice-header-row" style={{ marginBottom: '40px' }}>
        <div>
          <div className="invoice-brand" style={{ fontSize: '28px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '4px' }}>
            Homecrowd
          </div>
          <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.6 }}>
            support@gethomecrowd.com
          </div>
        </div>
        <div className="invoice-header-right" style={{ textAlign: 'right' }}>
          <div className="invoice-title" style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '4px' }}>
            {documentTitle}
          </div>
          <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.6, wordBreak: 'break-word' }}>
            <div>#{documentNumber || invoiceNumberFor(yearMonth, merchantName)}</div>
            <div>{monthRangeLabel(yearMonth)}</div>
          </div>
        </div>
      </div>

      <div className="invoice-billto-grid" style={{ marginBottom: '40px', paddingBottom: '32px', borderBottom: '1px solid #f0f0f0' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999', marginBottom: '8px' }}>
            {partyLabel}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '2px', wordBreak: 'break-word' }}>{merchantName}</div>
          <div style={{ fontSize: '13px', color: '#666' }}>{periodLabel}: {monthTitle(yearMonth)}</div>
          {note && <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>{note}</div>}
        </div>
        <div className="invoice-amount-due" style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999', marginBottom: '8px' }}>
            {amountLabel}
          </div>
          <div className="invoice-amount-due-value" style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em' }}>
            {formatMoney(totals.totalCommission + feeAmount)}
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
            Rate {formatPct(totals.blendedRate)} · {totals.count} transaction{totals.count === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <div className="invoice-table-scroll" style={{ overflowX: 'auto' }}>
        <table className="invoice-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'auto' }}>
          <colgroup>
            <col style={{ width: evenColumns ? evenWidth : '1%' }} />
            {showMerchantColumn && <col style={evenColumns ? { width: evenWidth } : undefined} />}
            {showBaseColumn && <col style={evenColumns ? { width: evenWidth } : undefined} />}
            {showRateColumn && <col style={evenColumns ? { width: evenWidth } : undefined} />}
            <col style={evenColumns ? { width: evenWidth } : undefined} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '1px solid #e9ecef' }}>
              <th style={headCell}>{dateColumnLabel}</th>
              {showMerchantColumn && <th style={headCell}>Merchant</th>}
              {showBaseColumn && <th style={headCellRight}>{baseColumnLabel}</th>}
              {showRateColumn && <th style={headCellRight}>Rate</th>}
              <th style={headCellRight}>{commissionLabel}</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 && (
              <tr>
                <td colSpan={columnCount} style={{ padding: '24px 8px', textAlign: 'center', color: '#999' }}>
                  {emptyText}
                </td>
              </tr>
            )}
            {lineItems.map((l) => (
              <tr key={l.id} style={{ borderBottom: '1px solid #f4f4f4' }}>
                <td style={{ padding: '12px 8px', color: '#444', whiteSpace: 'nowrap' }}>
                  {l.date ? new Date(l.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </td>
                {showMerchantColumn && (
                  <td style={{ padding: '12px 8px', color: '#444' }}>{l.merchant || '—'}</td>
                )}
                {showBaseColumn && (
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#444' }}>{formatMoney(l.amount)}</td>
                )}
                {showRateColumn && (
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#666' }}>{formatPct(l.rate)}</td>
                )}
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>{formatMoney(l.commission)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {feeLabel && (
              <>
                <tr style={{ borderTop: '2px solid #1a1a1a' }}>
                  <td colSpan={columnCount - 1} style={{ padding: '14px 8px', fontSize: '13px', textAlign: 'right', color: '#666' }}>
                    Subtotal
                  </td>
                  <td style={{ padding: '14px 8px', textAlign: 'right', fontWeight: 600 }}>
                    {formatMoney(totals.totalCommission)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={columnCount - 1} style={{ padding: '4px 8px 14px', fontSize: '13px', textAlign: 'right', color: '#666' }}>
                    {feeLabel}
                  </td>
                  <td style={{ padding: '4px 8px 14px', textAlign: 'right', color: '#666' }}>
                    {formatMoney(feeAmount)}
                  </td>
                </tr>
              </>
            )}
            <tr style={{ borderTop: feeLabel ? '1px solid #1a1a1a' : '2px solid #1a1a1a' }}>
              <td colSpan={showMerchantColumn ? 2 : 1} style={{ padding: '16px 8px', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Total
              </td>
              {showBaseColumn && !feeLabel && (
                <td style={{ padding: '16px 8px', textAlign: 'right', fontWeight: 600 }}>{formatMoney(totals.totalSales)}</td>
              )}
              {showRateColumn && !feeLabel && (
                <td style={{ padding: '16px 8px', textAlign: 'right', color: '#666' }}>{formatPct(totals.blendedRate)}</td>
              )}
              {feeLabel && <td colSpan={columnCount - (showMerchantColumn ? 3 : 2)} />}
              <td style={{ padding: '16px 8px', textAlign: 'right', fontSize: '16px', fontWeight: 700 }}>
                {formatMoney(totals.totalCommission + (feeLabel ? feeAmount : 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </>
);
};

export const InvoicePrintStyles = () => (
  <style>{`
    @media print {
      @page {
        margin: 0.5in 0.5in 0.75in 0.5in;
        @top-left { content: ""; }
        @top-center { content: ""; }
        @top-right { content: ""; }
        @bottom-center { content: ""; }
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 9pt;
          color: #888;
        }
      }
      .admin-sidebar,
      .admin-sidebar-toggle,
      .admin-sidebar-backdrop,
      .invoice-actions {
        display: none !important;
      }
      /* The staff shell is a fixed-height app, not a document: .admin-dashboard
         is height:100vh with overflow:hidden and .admin-main is the scroll
         container inside it (styles/StaffNavbar.css). Printed as-is that
         yields exactly one viewport — the rest of the rows are clipped rather
         than flowing onto page 2. Unpin the whole shell so the sheet is free to
         paginate. */
      .admin-dashboard,
      .admin-main,
      .admin-content {
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
      }
      .admin-main {
        margin-left: 0 !important;
        padding: 0 !important;
      }
      .invoice-sheet {
        border: none !important;
        padding: 0 !important;
      }
      /* A scroll container does not paginate: in paged media it clips its
         overflow instead of breaking across pages, so on a sheet taller than
         one page whatever sits at the bottom of the table is silently dropped
         — the subtotal, the Stripe fee and the total. The horizontal scroll is
         a small-screen affordance and has nothing to do with paper. */
      .invoice-table-scroll {
        overflow: visible !important;
      }
      /* Below 768px the staff stylesheet re-renders tables as scrollable blocks
         (styles/StaffPages.css). A printed Letter page is only ~720px of CSS
         pixels, so that mobile rule silently applies to paper — and a table set
         to display:block wraps its rows in an anonymous table box that shrinks
         to its content, leaving the right of the sheet empty. Put it back to a
         real full-width table. */
      .admin-content table,
      .invoice-table {
        display: table !important;
        width: 100% !important;
        max-width: none !important;
        overflow: visible !important;
      }
      /* Keep the totals together rather than let a page break land between the
         fee and the total it is part of, and never split a line item across
         two pages. */
      .invoice-table tfoot,
      .invoice-table tfoot tr,
      .invoice-table tbody tr {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      /* A tfoot is a *repeating* footer group in paged media, so once the sheet
         runs to two pages the subtotal, fee and total would print at the foot
         of every one. These totals are struck once, at the end. */
      .invoice-table tfoot {
        display: table-row-group;
      }
      /* The header, by contrast, should repeat: a continuation page of line
         items is unreadable without its column labels. */
      .invoice-table thead {
        display: table-header-group;
      }
    }
  `}</style>
);

export default InvoiceSheet;
